/**
 * Reject a pending promise (e.g. a Supabase query) if it doesn't settle in time.
 *
 * Supabase/fetch requests have no client-side timeout, so on flaky mobile
 * networks a request can stall indefinitely — neither resolving nor rejecting.
 * Any `await` on it then hangs forever, leaving loading spinners spinning.
 * Wrapping the query rejects with a TimeoutError so callers' catch blocks run
 * and the UI can recover instead of hanging.
 */
export class TimeoutError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "TimeoutError";
  }
}

export const DEFAULT_QUERY_TIMEOUT_MS = 15000;

export function withTimeout<T>(
  promise: PromiseLike<T>,
  ms: number = DEFAULT_QUERY_TIMEOUT_MS,
  label = "request"
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new TimeoutError(`${label} timed out after ${ms}ms`));
    }, ms);

    Promise.resolve(promise).then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}
